import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
function Modal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-180">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-181">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" data-id="element-182" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25
      }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-183">
            <div className="flex items-center justify-between p-6 border-b border-stone-100" data-id="element-184">
              <h2 className="text-xl font-bold text-stone-900" data-id="element-185">{title}</h2>
              <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors text-stone-500 hover:text-stone-800" data-id="element-186">
                <X className="w-5 h-5" data-id="element-187" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)] text-stone-700 text-sm leading-relaxed space-y-4" data-id="element-188">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-stone-900 py-10" data-id="element-189">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-190">
          <div className="flex flex-col items-center gap-4" data-id="element-191">
            <div className="flex items-center gap-6" data-id="element-192">
              <button onClick={() => setActiveModal('privacy')} className="text-sm text-stone-400 hover:text-white transition-colors underline-offset-4 hover:underline cursor-pointer" data-id="element-193">
                Política de Privacidad
              </button>
              <span className="text-stone-700" data-id="element-194">|</span>
              <button onClick={() => setActiveModal('terms')} className="text-sm text-stone-400 hover:text-white transition-colors underline-offset-4 hover:underline cursor-pointer" data-id="element-195">
                Términos y Condiciones
              </button>
            </div>
            <p className="text-stone-500 text-sm text-center" data-id="element-196">
              © {new Date().getFullYear()} Protocolo Anti-Inflamación. Todos
              los derechos reservados.
            </p>
            <p className="text-xs text-stone-600 text-center max-w-lg" data-id="element-197">
              Este producto no sustituye el consejo médico profesional. Consultá
              a tu médico antes de comenzar cualquier programa de salud.
            </p>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-198">
        <p className="font-semibold text-stone-900" data-id="element-199">
          Última actualización: Abril 2026
        </p>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-200">
          1. Información que recopilamos
        </h3>
        <p data-id="element-201">
          Recopilamos la información personal que nos proporcionás
          voluntariamente al realizar una compra, incluyendo: nombre, dirección
          de correo electrónico y datos de pago procesados de forma segura a
          través de Mercado Pago.
        </p>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-202">
          2. Uso de la información
        </h3>
        <p data-id="element-203">Utilizamos tu información exclusivamente para:</p>
        <ul className="list-disc pl-5 space-y-1" data-id="element-204">
          <li data-id="element-205">Procesar tu compra y entregar el producto digital adquirido.</li>
          <li data-id="element-206">Enviarte comunicaciones relacionadas con tu compra.</li>
          <li data-id="element-207">Mejorar nuestros productos y servicios.</li>
        </ul>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-208">
          3. Protección de datos
        </h3>
        <p data-id="element-209">
          Tu información personal está protegida mediante protocolos de
          seguridad estándar de la industria. No almacenamos datos de tarjetas
          de crédito ni información financiera sensible. Todos los pagos son
          procesados de forma segura por Mercado Pago.
        </p>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-210">
          4. Compartir información
        </h3>
        <p data-id="element-211">
          No vendemos, intercambiamos ni transferimos tu información personal a
          terceros. La única excepción son los proveedores de servicios de
          confianza que nos asisten en la operación del sitio web y el
          procesamiento de pagos.
        </p>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-212">5. Cookies</h3>
        <p data-id="element-213">
          Utilizamos cookies para mejorar tu experiencia de navegación y
          analizar el tráfico del sitio. Podés configurar tu navegador para
          rechazar cookies, aunque esto podría afectar algunas funcionalidades.
        </p>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-214">
          6. Tus derechos
        </h3>
        <p data-id="element-215">
          Tenés derecho a acceder, rectificar o eliminar tu información personal
          en cualquier momento. Para ejercer estos derechos, podés contactarnos
          a través de los canales disponibles en nuestro sitio.
        </p>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-216">
          7. Cambios en esta política
        </h3>
        <p data-id="element-217">
          Nos reservamos el derecho de actualizar esta política de privacidad en
          cualquier momento. Los cambios serán publicados en esta página con la
          fecha de última actualización.
        </p>
      </Modal>

      {/* Terms & Conditions Modal */}
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-218">
        <p className="font-semibold text-stone-900" data-id="element-219">
          Última actualización: Abril 2026
        </p>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-220">
          1. Aceptación de los términos
        </h3>
        <p data-id="element-221">
          Al acceder y utilizar este sitio web y adquirir nuestros productos,
          aceptás estos términos y condiciones en su totalidad. Si no estás de
          acuerdo con alguno de estos términos, te pedimos que no utilices
          nuestros servicios.
        </p>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-222">
          2. Descripción del producto
        </h3>
        <p data-id="element-223">
          El Protocolo Anti-Inflamación es un producto digital educativo que
          incluye guías, planes alimenticios y material complementario. Este
          producto tiene fines informativos y educativos, y no constituye
          asesoramiento médico profesional.
        </p>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-224">
          3. Entrega del producto
        </h3>
        <p data-id="element-225">
          Al completar la compra, recibirás acceso inmediato al contenido
          digital a través del correo electrónico proporcionado durante el
          proceso de pago. Es tu responsabilidad asegurarte de que la dirección
          de correo sea correcta.
        </p>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-226">
          4. Política de reembolso
        </h3>
        <p data-id="element-227">
          Debido a la naturaleza digital del producto, no se ofrecen reembolsos
          una vez que se ha entregado el acceso al contenido. Te recomendamos
          leer toda la información disponible antes de realizar tu compra.
        </p>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-228">
          5. Propiedad intelectual
        </h3>
        <p data-id="element-229">
          Todo el contenido incluido en el Protocolo Anti-Inflamación (textos,
          imágenes, diseños, metodologías) está protegido por derechos de autor.
          Queda prohibida su reproducción, distribución o reventa sin
          autorización expresa.
        </p>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-230">
          6. Limitación de responsabilidad
        </h3>
        <p data-id="element-231">
          Los resultados pueden variar de persona a persona. No garantizamos
          resultados específicos. Este producto no reemplaza el consejo médico
          profesional. Consultá a tu médico antes de realizar cambios en tu
          alimentación o estilo de vida.
        </p>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-232">
          7. Uso adecuado
        </h3>
        <p data-id="element-233">
          El contenido adquirido es para uso personal y exclusivo del comprador.
          No está permitido compartir, redistribuir o comercializar el material
          sin autorización previa por escrito.
        </p>

        <h3 className="font-bold text-stone-900 text-base mt-6" data-id="element-234">
          8. Modificaciones
        </h3>
        <p data-id="element-235">
          Nos reservamos el derecho de modificar estos términos y condiciones en
          cualquier momento. Las modificaciones entrarán en vigencia desde su
          publicación en el sitio web.
        </p>
      </Modal>
    </>;
}